import {
  isArray,
  isObject,
  ISODateType,
  TypeGuard,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { Gender } from '../../../patient/patient';
import {
  AccDiagnosisCodeSystemCode,
  AccEmploymentStatusCode,
  AccEthnicityCode,
  AccGeoLocationCode,
  AccOccupationCode,
  AccProviderTypeCode,
  AccRehabilitationCode,
  AccSceneCode,
  AccSportsCode,
  AccWorkTypeCode,
} from '../acc-code-tables';
import {
  AccAddressType,
  AccAlveolusInjury,
  AccBooleanValue,
  AccCariesActivity,
  AccClaimCoverStatus,
  AccClaimDiagnosisAction,
  AccClaimDiagnosisSide,
  AccCondition,
  AccDeglovingInjury,
  AccGender,
  AccIncapacityType,
  AccJawInjury,
  AccPhysicalRestriction,
  AccProsthesisType,
  AccSoftTissueHow,
  AccTmjInjury,
  AccToothInjuryClassification,
  AccToothPreAccidentCondition,
} from './acc-claim-enums';

export interface IAccClaim {
  pmsSoftwareName?: string;
  pmsSoftwareVersion?: string;
  claimNumber?: string; // API optional
  vendor?: IAccClaimVendor;
  provider?: IAccProvider;
  patient?: IAccPatient;
  employment?: IAccEmployment;
  injury?: IAccInjury;
  dentalDiagnosis?: IAccDentalDiagnosis; // API optional
  workCapacity?: IAccWorkCapacity; // API optional
  referrals?: IAccReferral[]; // API optional
  declaration?: IAccClaimDeclaration;
}

export const isValidAccClaim = TypeGuard.interface<IAccClaim>({
  pmsSoftwareName: isString,
  pmsSoftwareVersion: isString,
  claimNumber: TypeGuard.undefinedOr(isString),
  vendor: isObject,
  provider: isObject,
  patient: isObject,
  employment: isObject,
  injury: isObject,
  dentalDiagnosis: TypeGuard.undefinedOr(isObject),
  workCapacity: TypeGuard.undefinedOr(isObject),
  referrals: TypeGuard.undefinedOr(isArray),
  declaration: isObject,
});

interface IAccClaimStatusDiagnosis {
  diagnosisAction?: AccClaimDiagnosisAction;
  injuryStatus?: string;
  resultError?: string;
  resultErrorCode?: string;
  snomedCode?: string;
  snomedCodeDescription?: string;
  diagnosisCodeType: AccDiagnosisCodeSystemCode;
  diagnosisCode: string; // see codetables
  diagnosisDescription: string;
  diagnosisSide: AccClaimDiagnosisSide;
}

export interface IAccClaimStatus {
  acc45Number: string;
  accidentDate: ISODateType;
  claimNumber: string;
  coverStatus: AccClaimCoverStatus;
  diagnoses: IAccClaimStatusDiagnosis[];
  injuryResolved?: string;
  injuryResolvedFromDate?: ISODateType;
  injuryResolvedToDate?: ISODateType;
  partialNHINumber?: string;
}

export type AccConfirmedClaim = Omit<IAccClaim, 'claimNumber'> & {
  claimNumber: string;
};

export interface IAccClaimDeclaration {
  providerDeclaration: ISODateType;
}

export interface IAccDateRange {
  fromDate: ISODateType;
  toDate: ISODateType;
}

export interface IAccReferral {
  providerTypeCode: AccProviderTypeCode;
  referralReason?: string;
}

export interface IAccDentalDiagnosis {
  teeth?: IAccTeeth;
  prosthesis: IAccProsthesis;
  softTissue?: IAccSoftTissue;
  jawAlveolusTmj?: IAccJawAlveolusTmj;
  other: IAccDentalInjuryOther;
}

export interface IAccDentalInjuryOther {
  permanentTeethMissingPriorToAccident: AccBooleanValue;
  teethMissing?: AccToothNumber[]; // Has to contain valid teeth numbers.
  oralHygiene: AccCondition;
  periodontalCondition: AccCondition;
  cariesActivityInMouth: AccCariesActivity;
  radiographsTaken: AccBooleanValue;
  radiographsType?: string; // Maximum length is 100 characters.
  photographicRecordOfInjury: AccBooleanValue;
}

export interface IAccSoftTissueInjury {
  how: AccSoftTissueHow[];
  positionInMouth?: string; // Max 100 characters
}

export interface IAccJawAlveolusTmj {
  alveolusInjuries?: AccAlveolusInjury[]; // needs injuries
  teethInvolved?: AccToothNumber[];
  jawInjuries?: AccJawInjury[];
  typePosition?: string; // Maximum length is 100 characters
  tmjInjuries?: AccTmjInjury[];
  specificInjury?: string; // Maximum length is 100
}

export interface IAccSoftTissue {
  gingiva?: IAccSoftTissueInjury;
  mucosa?: IAccSoftTissueInjury;
  lip?: IAccSoftTissueInjury;
  deglovingInjury?: AccDeglovingInjury[];
}

export interface IAccTeeth {
  injuries: IAccToothInjury[];
  additionalInjuryComment?: string; // Max 500 characters
}

export interface IAccToothInjury {
  toothNumber: AccToothNumber;
  injuryClassification: AccToothInjuryClassification[];
  preAccidentCondition: AccToothPreAccidentCondition[];
}

export interface IAccProsthesis {
  wornAtTimeOfInjury: AccBooleanValue;
  prosthesisType?: AccProsthesisType[];
  teethOnPartialDenture?: AccToothNumber[];
}

export interface IAccWorkCapacity {
  canResumeNormalWork: AccBooleanValue;
  incapacity?: IAccIncapacity[]; // Only required if canResumeNormalWork is false
  returnToNormalWorkDate?: ISODateType;
}

export interface IAccIncapacity {
  incapacityType: AccIncapacityType;
  incapacityDateRange: IAccDateRange;
  selectedAlternativeWork?: IAccSelectedAlternativeWork;
}

export interface IAccSelectedAlternativeWork {
  alternativeWorkTypeCode?: AccWorkTypeCode;
  physicalRestrictions?: AccPhysicalRestriction[];
  restrictedHoursPerDay?: number;
  restrictionComment?: string;
}

export interface IAccAddress {
  type: AccAddressType;
  line1: string;
  line2?: string;
  suburb?: string;
  city: string;
  postCode: string;
  country: string;
}

export interface IAccInjury {
  accidentDate: ISODateType;
  accidentLocationCode: AccGeoLocationCode;
  accidentSceneCode: AccSceneCode;
  causeOfAccident: string; // For the specific event
  involvesVehicle: AccBooleanValue;
  medicalTreatmentInjury?: AccBooleanValue;
  workInjury: AccBooleanValue;
  sportingInjury: AccBooleanValue;
  sportNameCode?: AccSportsCode; // Only required if sportingInjury=true
  gradualProcessInjury?: AccBooleanValue; // Legacy field. This value is no longer used in claim processing.
  admittedToHospital?: AccBooleanValue; // Only DHBs can set this to true
  injuryComments?: string;
  assistanceRequired?: AccBooleanValue; // Only DHBs can set this to true
  accContactProvider?: AccRehabilitationCode;
}

export interface IAccClaimVendor {
  practiceName: string;
  hpiOrganisationNumber: string; // ACC vendorId
  hpiFacilityNumber?: string; // ACC facilityId
}

export interface IAccProvider {
  address: IAccAddress;
  details: IAccClaimProviderDetails;
}

export interface IAccPatient {
  details: IAccClaimPatientDetails;
  address: IAccAddress;
  occupationCode?: AccOccupationCode;
  contact?: IAccContact;
}

export interface IAccContact {
  emailAddress?: string; // Must be in valid email address format
  mobilePhone?: string; // Include the network code but no spaces or parentheses eg 0211234567
  homePhone?: string; // Include the area code, but no spaces or parentheses eg 041234567
  workPhone?: string; // Include the area code, but no spaces or parentheses eg 041234567
}

export interface IAccClaimPatientDetails {
  nhi?: string;
  firstName: string;
  middleName?: string;
  surname: string;
  dateOfBirth: ISODateType;
  gender: AccGender;
  ethnicityCode: AccEthnicityCode;
  otherEthnicity?: string; // Only required when ethnicityCode is 54/Other
}

export interface IAccClaimProviderDetails {
  providerId: string;
  providerTypeCode: AccProviderTypeCode;
  firstName: string;
  middleName?: string;
  surname: string;
}

export interface IAccEmployment {
  inPaidEmployment: AccBooleanValue;
  employmentStatusCode?: AccEmploymentStatusCode; // Only required if inPaidEmployment is true
  otherEmployment?: string; // Only required if employmentStatusCode is 5 (other)
  workTypeCode?: AccWorkTypeCode;
  employer?: IAccClaimEmployer;
}

export interface IAccClaimEmployer {
  employerName?: string; // Required when inPaidEmployment is true
  address?: IAccAddress;
}

export type AccToothNumber =
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 31
  | 32
  | 33
  | 34
  | 35
  | 36
  | 37
  | 38
  | 41
  | 42
  | 43
  | 44
  | 45
  | 46
  | 47
  | 48
  | 51
  | 52
  | 53
  | 54
  | 55
  | 56
  | 57
  | 58
  | 61
  | 62
  | 63
  | 64
  | 65
  | 66
  | 67
  | 68
  | 71
  | 72
  | 73
  | 74
  | 75
  | 76
  | 77
  | 78
  | 81
  | 82
  | 83
  | 84
  | 85
  | 86
  | 87
  | 88;

export const PRINCIPLE_TO_ACC_GENDER_MAP: Record<Gender, AccGender> = {
  [Gender.Male]: AccGender.Male,
  [Gender.Female]: AccGender.Female,
  [Gender.Other]: AccGender.Other,
  [Gender.NotSpecified]: AccGender.Unknown,
};
